import {AGE_GROUPS} from '@/constants';
import {
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent
} from '@mui/material';
import {useCallback} from 'react';
import {useTranslation} from 'react-i18next';

interface AgeGroupFilterProps {
  selectedAgeGroups: string[];
  onAgeGroupChange: (selected: string[]) => void;
}

const AgeGroupFilter: React.FC<AgeGroupFilterProps> = ({
  selectedAgeGroups,
  onAgeGroupChange
}) => {
  const {t} = useTranslation();

  const ageGroupsAll = AGE_GROUPS.map((group) => ({
    value: group.value,
    label:
      group.value === 'ALL'
        ? t(group.labelKey)
        : `${t(group.labelKey)} (${group.ageRange} ${t('year_abbr')})`
  }));

  const handleChange = useCallback(
    (event: SelectChangeEvent<string[]>) => {
      const value = event.target.value as string[];
      if (value.includes('ALL')) {
        if (selectedAgeGroups.includes('ALL')) {
          const valuesToSelect = value.filter((group) => group !== 'ALL');
          onAgeGroupChange(valuesToSelect);
        } else {
          onAgeGroupChange(['ALL']);
        }
      } else {
        if (value.length > 0) {
          onAgeGroupChange(value.filter((v) => v !== 'ALL'));
        } else {
          onAgeGroupChange(['ALL']);
        }
      }
    },
    [selectedAgeGroups, onAgeGroupChange]
  );

  return (
    <FormControl fullWidth>
      <InputLabel shrink id="age-group-select-label">
        {t('ageGroups_multi')}
      </InputLabel>
      <Select
        labelId="age-group-select-label"
        label={t('ageGroups_multi')}
        multiple
        value={selectedAgeGroups}
        onChange={handleChange}
        renderValue={(selected) =>
          (selected as string[])
            .map((value) => {
              const group = ageGroupsAll.find((g) => g.value === value);
              return group ? group.label : value;
            })
            .join(', ')
        }
      >
        {ageGroupsAll.map((group) => (
          <MenuItem key={group.value} value={group.value}>
            <Checkbox checked={selectedAgeGroups.includes(group.value)} />
            <ListItemText primary={group.label} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default AgeGroupFilter;
